import type { Ref } from 'vue'
import type {
  CreatePaymentRequest,
  CreatePaymentResponse,
  PaymentHistoryResponse,
  PaymentOrder,
  UpgradePricePreview,
} from './types'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { computed, unref } from 'vue'
import { apiClient } from '@/shared/api'

export function createPaymentRequest(data: CreatePaymentRequest) {
  return apiClient.post<CreatePaymentResponse>('/payment/create/', data)
}

export function useCreatePaymentMutation() {
  return useMutation({
    mutationFn: createPaymentRequest,
  })
}

export function upgradePaymentRequest(data: CreatePaymentRequest) {
  return apiClient.post<CreatePaymentResponse>('/payment/upgrade/', data)
}

export function useUpgradePaymentMutation() {
  return useMutation({
    mutationFn: upgradePaymentRequest,
  })
}

export function fetchUpgradePreview(params: CreatePaymentRequest) {
  return apiClient.get<UpgradePricePreview>('/payment/upgrade-preview/', {
    params,
  })
}

export function useUpgradePreviewQuery(
  params: Ref<CreatePaymentRequest | null>,
) {
  return useQuery({
    queryKey: ['payment-upgrade-preview', params],
    queryFn: () => fetchUpgradePreview(unref(params)!),
    enabled: computed(() => !!unref(params)),
    staleTime: 30 * 1000,
  })
}

export function fetchPaymentStatus(invoiceId: string) {
  return apiClient.get<PaymentOrder>(`/payment/status/${invoiceId}/`)
}

export function usePaymentStatusQuery(invoiceId: Ref<string | null>) {
  return useQuery({
    queryKey: ['payment-status', invoiceId],
    queryFn: () => fetchPaymentStatus(unref(invoiceId)!),
    enabled: computed(() => !!unref(invoiceId)),
    staleTime: 0,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === 'paid' || status === 'failed' || status === 'expired')
        return false
      return 3000
    },
  })
}

export function fetchPaymentHistory() {
  return apiClient.get<PaymentHistoryResponse>('/payment/my-tariffs/')
}

export function usePaymentHistoryQuery() {
  return useQuery({
    queryKey: ['payment-history'],
    queryFn: fetchPaymentHistory,
    staleTime: 30 * 1000,
  })
}
